/* eslint-disable @typescript-eslint/no-explicit-any */
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import { DynamicFormProps } from '@/shared/components/DynamicForm/DynamicForm'
import { useSelectedLanguagesQuery } from '@/features/menu-management/SidebarAccordion/hooks/useSelectedLanguagesQuery'
import { buildSyncFieldsValidationSchema } from '@/features/menu-management/helpers/buildSyncFieldsValidationSchema'
import {
  AutoTranslateButton,
  AutoTranslateFormEffect
} from '@/features/menu-management/helpers/AutoTranslateFormEffect'
import { useIngredientsQuery } from './useIngredientsQuery'
import { currencySymbol } from '@/shared/utils/price'

export const useIngredientsFormConfig = () => {
  const { t } = useTranslation()
  const { allergensList, unitsList } = useIngredientsQuery()

  const currentLanguage =
    useTranslation().i18n.language || localStorage.getItem('i18nextLng') || ''
  const { defaultLanguageOption, selectedLanguagesOptions } =
    useSelectedLanguagesQuery(currentLanguage)

  const mappedAllergenList =
    allergensList.data?.map(allergen => ({
      label: allergen.translations?.find(
        translation => translation.languageCode === defaultLanguageOption?.value
      )?.name,
      iconImage: allergen.iconUrl,
      value: allergen.id
    })) || []

  const mappedUnitsList =
    unitsList.data?.map(unit => ({
      label: unit.translations?.find(
        translation => translation.languageCode === defaultLanguageOption?.value
      )?.name,
      value: unit.id
    })) || []

  const formConfig = {
    id: `menu-type-form`,
    FormEffects: <AutoTranslateFormEffect />,
    // Rendered by the modal in its action row rather than beside the language
    // tabs: a translation helper belongs with confirm, not wedged into the
    // form's own controls.
    footerAction: (
      <AutoTranslateButton
        defaultLanguage={defaultLanguageOption?.value || 'en'}
        allLanguages={selectedLanguagesOptions.map(o => o.value as string)}
        syncFieldNames={['ingredientName']}
      />
    ),
    fields: [
      {
        name: 'translations',
        label: t('TOPPING_DETAILS_MODAL.TRANSLATIONS.DESCRIPTION'),
        type: 'label'
      },
      {
        name: 'ingredientLanguage',
        type: 'tabs',
        tabs: selectedLanguagesOptions,
        isSyncFormField: true,
        hideClearIcon: true
      },
      {
        name: 'ingredientName',
        label: t('DETAILS_TILE.MENU.NAME.LABEL'),
        placeholder: t('DETAILS_TILE.MENU.NAME.PLACEHOLDER'),
        type: 'text',
        focused: true,
        required: true,
        validation: buildSyncFieldsValidationSchema(selectedLanguagesOptions),
        syncFormField: 'ingredientLanguage'
      },
      {
        type: 'input-group',
        inputs: [
          {
            name: 'price',
            label: t('DETAILS_TILE.MENU.PRICE.LABEL'),
            placeholder: t('DETAILS_TILE.MENU.PRICE.PLACEHOLDER'),
            type: 'text',
            focused: true,
            endAdornment: currencySymbol(),
            validation: z
              .union([
                z.string().transform(x =>
                  // Comma is the decimal separator people type here
                  x.replace(',', '.').replace(/[^0-9.-]+/g, '')
                ),
                z.number()
              ])
              .pipe(z.coerce.number().min(0.01).max(999999999))
          },
          {
            name: 'ingredientAllergens',
            label: t('DETAILS_TILE.MENU.ALLERGENS.LABEL'),
            placeholder: t('DETAILS_TILE.MENU.ALLERGENS.PLACEHOLDER'),
            type: 'multi-select',
            focused: true,
            options: mappedAllergenList || []
          }
        ]
      },
      {
        type: 'input-group',
        inputs: [
          {
            name: 'quantity',
            label: t('DETAILS_TILE.MENU.QUANTITY.LABEL'),
            placeholder: t('DETAILS_TILE.MENU.QUANTITY.PLACEHOLDER'),
            type: 'text',
            focused: true,
            validation: z
              .union([
                z.string().transform(x => x.replace(/[^0-9.-]+/g, '')),
                z.number()
              ])
              .pipe(z.coerce.number().min(0.0001).max(999999999))
          },
          {
            name: 'ingredientUnit',
            label: t('DETAILS_TILE.MENU.UNIT.LABEL'),
            placeholder: t('DETAILS_TILE.MENU.UNIT.PLACEHOLDER'),
            type: 'select',
            focused: true,
            options: mappedUnitsList || []
          }
        ]
      }
    ]
  }
  return formConfig as DynamicFormProps
}
